'use client'
import { logger } from '@/lib/logger';

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import SWSNeuralSynthesizer from './SWSNeuralSynthesizer'
import SWSWebGPUAccelerator from './SWSWebGPUAccelerator'
import SWSAIStyleTransfer from './SWSAIStyleTransfer'

interface SWSCollaborativeStudioProps {
  className?: string
}

interface Collaborator {
  id: string
  name: string
  color: string
  isActive: boolean
  lastActivity: number
  parameters: any
}

interface CollaborativeSession {
  id: string
  name: string
  collaborators: Collaborator[]
  masterParameters: any
  chatMessages: ChatMessage[]
  isRecording: boolean
  startTime: number
}

interface ChatMessage {
  id: string
  userId: string
  userName: string
  message: string
  timestamp: number
  type: 'message' | 'system' | 'parameter_change'
}

const SWSCollaborativeStudio: React.FC<SWSCollaborativeStudioProps> = ({ className = '' }) => {
  const [session, setSession] = useState<CollaborativeSession | null>(null)
  const [currentUser, setCurrentUser] = useState<Collaborator>({
    id: 'user_' + Math.random().toString(36).substr(2, 9),
    name: 'Anonymous Producer',
    color: '#' + Math.floor(Math.random()*16777215).toString(16),
    isActive: true,
    lastActivity: Date.now(),
    parameters: {}
  })

  const [isConnected, setIsConnected] = useState(false)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [activeView, setActiveView] = useState<'synthesis' | 'gpu' | 'transfer'>('synthesis')

  const wsRef = useRef<WebSocket | null>(null)
  const rtcPeerConnectionRef = useRef<RTCPeerConnection | null>(null)
  const localStreamRef = useRef<MediaStream | null>(null)
  const remoteAudioRef = useRef<HTMLAudioElement>(null)

  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null)
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)

  // WebRTC configuration for audio streaming - memoized for stability
  const rtcConfig = useMemo(() => ({
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  }), [])

  // Initialize WebRTC for audio streaming
  const initializeWebRTC = useCallback(async () => {
    try {
      // Get user media (audio only)
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      })

      // Create RTCPeerConnection
      const peerConnection = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' }
        ]
      })

      // Add audio track
      stream.getAudioTracks().forEach(track => {
        peerConnection.addTrack(track, stream)
      })

      // Handle remote audio
      peerConnection.ontrack = (event) => {
        const remoteAudio = new Audio()
        remoteAudio.srcObject = event.streams[0]
        remoteAudio.play()
      }

      setPeerConnection(peerConnection)
      setLocalStream(stream)

      logger.log('🎙️ WebRTC initialized successfully')
    } catch (error) {
      logger.error('❌ WebRTC initialization failed:', error)
    }
  }, [])

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data: any) => {
    switch (data.type) {
      case 'session_update':
        setSession(data.session)
        break

      case 'user_joined':
        setChatMessages(prev => [...prev, {
          id: Math.random().toString(36),
          userId: 'system',
          userName: 'System',
          message: `${data.user.name} joined the session`,
          timestamp: Date.now(),
          type: 'system'
        }])
        break

      case 'user_left':
        setChatMessages(prev => [...prev, {
          id: Math.random().toString(36),
          userId: 'system',
          userName: 'System',
          message: `${data.user.name} left the session`,
          timestamp: Date.now(),
          type: 'system'
        }])
        break

      case 'parameter_change':
        // Update local parameters from remote collaborator
        setChatMessages(prev => [...prev, {
          id: Math.random().toString(36),
          userId: data.userId,
          userName: data.userName,
          message: `Changed ${data.parameter} to ${data.value}`,
          timestamp: Date.now(),
          type: 'parameter_change'
        }])
        break

      case 'chat_message':
        setChatMessages(prev => [...prev, data.message])
        break
    }
  }, [])

  // WebSocket connection for real-time collaboration
  const connectWebSocket = useCallback((sessionId: string) => {
    const ws = new WebSocket(`wss://your-signaling-server.com/session/${sessionId}`)

    ws.onopen = () => {
      logger.log('🔗 Connected to collaborative session')
      setIsConnected(true)

      // Send join message
      ws.send(JSON.stringify({
        type: 'join',
        user: currentUser,
        sessionId
      }))
    }

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data)
      handleWebSocketMessage(data)
    }

    ws.onclose = () => {
      logger.log('🔌 Disconnected from session')
      setIsConnected(false)
    }

    wsRef.current = ws
  }, [currentUser, handleWebSocketMessage])

  // Join existing session
  const joinSession = useCallback(async (sessionId: string) => {
    connectWebSocket(sessionId)
    initializeWebRTC()
  }, [connectWebSocket, initializeWebRTC])

  // Send parameter changes to collaborators
  const broadcastParameterChange = useCallback((parameter: string, value: any) => {
    if (wsRef.current && isConnected) {
      wsRef.current.send(JSON.stringify({
        type: 'parameter_change',
        userId: currentUser.id,
        userName: currentUser.name,
        parameter,
        value
      }))
    }
  }, [currentUser, isConnected])

  // Send chat message
  const sendChatMessage = useCallback(() => {
    if (!newMessage.trim() || !wsRef.current || !isConnected) return

    const message: ChatMessage = {
      id: Math.random().toString(36),
      userId: currentUser.id,
      userName: currentUser.name,
      message: newMessage,
      timestamp: Date.now(),
      type: 'message'
    }

    wsRef.current.send(JSON.stringify({
      type: 'chat_message',
      message
    }))

    setChatMessages(prev => [...prev, message])
    setNewMessage('')
  }, [newMessage, currentUser, isConnected])

  // Initialize collaborative session
  const createSession = useCallback(async () => {
    const newSession: CollaborativeSession = {
      id: 'session_' + Math.random().toString(36).substr(2, 9),
      name: `SWS Session ${new Date().toLocaleTimeString()}`,
      collaborators: [currentUser],
      masterParameters: {},
      chatMessages: [],
      isRecording: false,
      startTime: Date.now()
    }

    setSession(newSession)
    connectWebSocket(newSession.id)
    initializeWebRTC()

    logger.log('🎵 Created collaborative session:', newSession.id)
  }, [currentUser, connectWebSocket, initializeWebRTC])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'Enter':
            if (event.shiftKey) {
              sendChatMessage()
            }
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [sendChatMessage])

  // Update user activity
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentUser(prev => ({ ...prev, lastActivity: Date.now() }))
    }, 30000) // Update every 30 seconds

    return () => clearInterval(interval)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
      const rtcPeerConnection = rtcPeerConnectionRef.current
      if (rtcPeerConnection) {
        rtcPeerConnection.close()
      }
      const localStream = localStreamRef.current
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  return (
    <div className={`bg-gradient-to-br from-blue-900/80 via-purple-900/80 to-indigo-900/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border border-blue-500/30 ${className}`}>
      <h1 className="text-4xl font-bold text-center mb-6 text-white drop-shadow-lg">
        🌐 Collaborative Studio
      </h1>

      <p className="text-center text-white/80 mb-8 max-w-2xl mx-auto">
        Real-time collaborative music synthesis with WebRTC audio streaming and live parameter synchronization.
        Work together with producers worldwide to create revolutionary sounds.
      </p>

      {/* Session Management */}
      <div className="mb-8">
        <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-cyan-300">🎵 Session Control</h3>
            <div className={`flex items-center gap-2 ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}></div>
              {isConnected ? 'Connected' : 'Disconnected'}
            </div>
          </div>

          {!session ? (
            <div className="text-center">
              <button
                onClick={createSession}
                className="px-8 py-4 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-bold rounded-2xl transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                🎵 Create New Session
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-white/80 text-sm">Session ID</div>
                <div className="text-cyan-300 font-mono text-sm">{session.id}</div>
              </div>
              <div>
                <div className="text-white/80 text-sm">Collaborators</div>
                <div className="text-purple-300 font-bold">{session.collaborators.length}</div>
              </div>
              <div>
                <div className="text-white/80 text-sm">Duration</div>
                <div className="text-pink-300 font-bold">
                  {Math.floor((Date.now() - session.startTime) / 60000)}m {Math.floor(((Date.now() - session.startTime) % 60000) / 1000)}s
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Collaborators List */}
      {session && (
        <div className="mb-8">
          <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-green-300 mb-4">👥 Collaborators</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {session.collaborators.map((collaborator) => (
                <div key={collaborator.id} className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded-full border-2 border-white/30"
                    style={{ backgroundColor: collaborator.color }}
                  ></div>
                  <div>
                    <div className="text-white font-medium text-sm">{collaborator.name}</div>
                    <div className={`text-xs ${collaborator.isActive ? 'text-green-400' : 'text-gray-400'}`}>
                      {collaborator.isActive ? 'Active' : 'Away'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tool Selection */}
      <div className="mb-8">
        <div className="bg-white/5 rounded-2xl p-6 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-orange-300 mb-4">🛠️ Collaborative Tools</h3>
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setActiveView('synthesis')}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                activeView === 'synthesis'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              🧠 Neural Synth
            </button>
            <button
              onClick={() => setActiveView('gpu')}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                activeView === 'gpu'
                  ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              🚀 WebGPU
            </button>
            <button
              onClick={() => setActiveView('transfer')}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                activeView === 'transfer'
                  ? 'bg-gradient-to-r from-green-500 to-teal-600 text-white shadow-lg'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              🎨 AI Transfer
            </button>
          </div>

          {/* Active Tool */}
          <div className="bg-black/30 rounded-xl p-6">
            {activeView === 'synthesis' && (
              <SWSNeuralSynthesizer />
            )}
            {activeView === 'gpu' && (
              <SWSWebGPUAccelerator />
            )}
            {activeView === 'transfer' && (
              <SWSAIStyleTransfer />
            )}
          </div>
        </div>
      </div>

      {/* Chat Interface */}
      <div className="mb-8">
        <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-pink-300 mb-4">💬 Live Chat</h3>

          {/* Messages */}
          <div className="h-48 overflow-y-auto mb-4 bg-black/20 rounded-lg p-4">
            {chatMessages.map((message) => (
              <div key={message.id} className="mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-white/60 text-sm">
                    {message.userName}:
                  </span>
                  <span className={`text-sm ${
                    message.type === 'system' ? 'text-yellow-400' :
                    message.type === 'parameter_change' ? 'text-cyan-400' :
                    'text-white'
                  }`}>
                    {message.message}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Message Input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
              placeholder="Type your message... (Ctrl+Enter to send)"
              className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-white/50"
              disabled={!isConnected}
            />
            <button
              onClick={sendChatMessage}
              disabled={!newMessage.trim() || !isConnected}
              className="px-6 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-200"
            >
              Send
            </button>
          </div>
        </div>
      </div>

      {/* Audio Streaming Status */}
      <div className="bg-white/5 rounded-2xl p-6 backdrop-blur-sm">
        <h3 className="text-xl font-bold text-teal-300 mb-4">🎙️ Audio Streaming</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-white/80 text-sm mb-2">Local Audio</div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
              <span className="text-green-400 text-sm">Active</span>
            </div>
          </div>
          <div>
            <div className="text-white/80 text-sm mb-2">Remote Audio</div>
            <audio ref={remoteAudioRef} autoPlay className="hidden" />
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-400"></div>
              <span className="text-blue-400 text-sm">Connected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SWSCollaborativeStudio